import { Delete, Get, Patch, Post } from "@/axios/AxiosFunctions";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/Core/Button";
import SearchInput from "@/components/Core/SearchInput";
import SideBarSkeleton from "@/components/Core/SideBarSkeleton";
import TableStructure from "@/components/Core/TableStructure";
import TableActionHandler from "@/components/TableActionHandler";
import { BaseURL } from "@/config/apiUrl";
import { recordsLimit } from "@/constant/constants";
import useDebounce from "@/customHooks/useDebounce";
import { brandManagementTableHeaders } from "@/data/data";
import { apiHeader, RenderToast } from "@/helper/HelperFunction";
import AddEditBrandModal from "@/modals/AddEditBrandModal";
import AreYouSureModal from "@/modals/AreYouSureModal";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import classes from "./BrandManagement.module.css";

const MODAL_STATES = {
  DELETE_BRAND: "DELETE_BRAND",
  CREATE_BRAND: "CREATE_BRAND",
  EDIT_BRAND: "EDIT_BRAND",
};

const BrandLoadingStates = {
  GET_BRANDS: "GET_BRANDS",
  DELETE_BRAND: "DELETE_BRAND",
  SUBMIT_BRAND: "SUBMIT_BRAND",
};

export default function BrandManagement() {
  const { access_token } = useSelector((state) => state.authReducer);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecord, setTotalRecord] = useState(0);
  const [showModal, setShowModal] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  // get brands
  const getData = async ({ _page = page, _search = debouncedSearch }) => {
    const queryParams = {
      page: _page,
      limit: recordsLimit,
      search: _search,
    };
    const apiUrl = BaseURL(
      `brands/admin/all?${new URLSearchParams(queryParams).toString()}`
    );
    setIsLoading(BrandLoadingStates.GET_BRANDS);
    const response = await Get(apiUrl, access_token);
    if (response) {
      setData(response?.data?.data?.brands);
      setTotalRecord(response?.data?.data?.totalCount);
    }
    setIsLoading(null);
  };
  //   add edit brand
  const handleAddEditBrand = async (params) => {
    const apiUrl = BaseURL(`brands`);
    setIsLoading(BrandLoadingStates.SUBMIT_BRAND);
    const response = selectedData
      ? await Patch(apiUrl, params, apiHeader(access_token))
      : await Post(apiUrl, params, apiHeader(access_token));
    if (response) {
      RenderToast({
        type: "success",
        message: selectedData
          ? "Brand updated successfully"
          : "Brand added successfully",
      });
      if (selectedData) {
        setData(
          data?.map((item) => {
            if (item?._id === selectedData?._id) {
              return { ...item, ...params };
            }
            return item;
          })
        );
      } else {
        setData([response?.data?.data, ...data]);
      }
      setShowModal(null);
      setSelectedData(null);
    }
    setIsLoading(null);
  };
  //   delete brand
  const handleDeleteBrand = async () => {
    const apiUrl = BaseURL(`brands/${selectedData?._id}`);
    setIsLoading(BrandLoadingStates.DELETE_BRAND);
    const response = await Delete(apiUrl, apiHeader(access_token));
    if (response) {
      RenderToast({
        type: "success",
        message: "Brand deleted successfully",
      });
      setData(data?.filter((item) => item?._id !== selectedData?._id));
      setTotalRecord((prev) => prev - 1);
      setSelectedData(null);
      setShowModal(null);
    }
    setIsLoading(null);
  };

  useEffect(() => {
    getData({
      _page: 1,
    });
  }, [debouncedSearch]);

  return (
    <SideBarSkeleton>
      <div className={classes.mainWrapper}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <h3>Brand Management</h3>
            <Breadcrumb />
          </div>
        </div>
        <div className={classes.searchWrapper}>
          <SearchInput
            placeholder="Search Brand..."
            customStyle={{
              height: "40px",
            }}
            value={search}
            setter={setSearch}
          />
          <Button
            onClick={() => {
              setShowModal(MODAL_STATES.CREATE_BRAND);
            }}
            variant="primary"
            label="Create Brand"
          />
        </div>

        <TableStructure
          headerTitle=""
          tableHeaders={brandManagementTableHeaders}
          isLoading={isLoading === BrandLoadingStates.GET_BRANDS}
          tableContent={data?.map((item) => ({
            ...item,
            actions: (
              <TableActionHandler
                onEdit={() => {
                  setSelectedData(item);
                  setShowModal(MODAL_STATES.EDIT_BRAND);
                }}
                onDelete={() => {
                  setSelectedData(item);
                  setShowModal(MODAL_STATES.DELETE_BRAND);
                }}
              />
            ),
          }))}
          page={page}
          setPage={(e) => {
            setPage(e);
            getData({
              _page: e,
            });
          }}
          totalRecord={totalRecord}
          recordLimit={recordsLimit}
          noDataMessage="No Brands Found"
          customStyle={{
            height: "calc(100vh - 294px)",
          }}
        />
        {[MODAL_STATES.CREATE_BRAND, MODAL_STATES.EDIT_BRAND].includes(
          showModal
        ) && (
          <AddEditBrandModal
            show={[MODAL_STATES.CREATE_BRAND, MODAL_STATES.EDIT_BRAND].includes(
              showModal
            )}
            setShow={() => {
              setShowModal(null);
              setSelectedData(null);
            }}
            selectedItem={selectedData}
            handleClick={handleAddEditBrand}
            modalLoading={isLoading === BrandLoadingStates.SUBMIT_BRAND}
          />
        )}
        {showModal === MODAL_STATES.DELETE_BRAND && (
          <AreYouSureModal
            show={showModal}
            setShow={() => {
              setShowModal(null);
              setSelectedData(null);
            }}
            title="Delete Brand"
            subTitle="Are you sure you want to delete this brand?"
            onClick={handleDeleteBrand}
            isApiCall={isLoading === BrandLoadingStates.DELETE_BRAND}
          />
        )}
      </div>
    </SideBarSkeleton>
  );
}
